import { i18n } from 'next-i18next';
import { lastValueFrom, of } from 'rxjs';
import { parseCookies, setCookie, destroyCookie } from 'nookies';

import {
  setIsMobileDevice,
  setIsGoogleBot,
  setIsSafari,
  setLayoutEspots,
  setNavBarMenu,
  setEmptyItemEspots,
  EmptyItemEspotType,
  setStoreIdForPrice
} from '@/redux/slices/gui.slice';
import { addItemToStorageWBOk, getCartStorageSuccess } from '@/redux/slices/storage.slice';
import { setBaseUrl, setRatings, setStoreConf } from '@/redux/slices/root.slice';
import { getWishListSuccess } from '@/redux/slices/wishList.slice';
import { connectedUserSuccess } from '@/redux/slices/auth.slice';
import { setActAsCustomer } from '@/redux/slices/admin.slice';
import { encodeUserEmail, encodeUserPickId, getUserSuccess } from '@/redux/slices/user.slice';
import { getCartSuccess } from '@/redux/slices/cart.slice';
import { getBoxSuccess } from '@/redux/slices/box.slice';
import { isUserEmpty } from '@/utils/comparisonUtils';
import { completePromises } from '@/utils/completePromises';
import { valueof } from '@/types';
import { PAGES } from '@/constants/pages';
import { ESPOTS } from '@/constants/espot';
import { HEADER_TYPE } from '@/constants/header';
import { FOOTER_TYPE } from '@/constants/footer';
import { STEPS } from '@/constants/checkout';
import { COOKIES } from '@/constants/cookies';
import { STORE_ID } from '@/constants/stores';

type RatingType = {
  ratingCount: number;
  ratingValue: number;
}
export const prefetchLayout = async (ctx, store, fullLayout = true) => {
  const isDebugLog = process.env.NEXT_PUBLIC_DEBUG_LOG === 'true';
  let isGoogleBotSaved: boolean;
  let storeConf: any;

  const log = (...args: any[]): void => {
    if (isDebugLog) {
      console.log(`MEP: PREF[${store.debugId}] LAYOUT`, ...args);
    }
  };

  try {
    const { dispatch, ajaxService, services } = store;

    const {
      productsService,
      storeService,
      authService,
      userService,
      boxService,
      cartService,
      espotService,
      storageService,
      externalService
    } = services;

    log('ENTER', store.debugId, ctx.req.url);

    // Add a random int (3 digits) to the request
    ctx.req.reqId = parseInt(`${Math.random() * 1000}`, 10);

    // Retrieve the connected user if any
    const { actAsCustomer, authToken, baseUrl, isMobile, isSafari, isGoogleBot } = ajaxService.getContext();
    const loggedUser = authService.getConnectedUser();
    const isConnected = !isUserEmpty(loggedUser);

    isGoogleBotSaved = isGoogleBot;

    const stepHT = [
      `${PAGES.CHECKOUT}/${STEPS.REVIEW}`,
      `${PAGES.CHECKOUT}/${STEPS.PAYMODE}`,
      `${PAGES.CHECKOUT}/${STEPS.REPORT}`,
      'stripes',
      'payjunction',
      'adyen',
      'hipay',
    ];

    log(
      'CONTEXT',
      store.debugId, baseUrl, actAsCustomer?._id, authToken?.slice(0, 10), loggedUser?.userId
    );

    let storeIdForPrice = parseCookies(ctx)[COOKIES.STOREID_FOR_PRICE.NAME];

    dispatch(setIsMobileDevice({ isMobileDevice: isMobile }));
    dispatch(setIsSafari({ isSafari }));
    dispatch(setBaseUrl({ baseUrl }));
    dispatch(setIsGoogleBot({ isGoogleBot }));

    // Fetch basic info from backend
    // If no locale or equals the fallback locale => get by host
    if (ctx.locale === ctx.defaultLocale) {
      storeConf = await lastValueFrom(storeService.getStoreConfByHost(ctx.req.headers.host, storeIdForPrice));
      if (storeConf.locale !== ctx.locale) {
        i18n?.changeLanguage(storeConf.locale);
      }
    } else if (ctx.locale.includes('-') > 0) {
      storeConf = await lastValueFrom(storeService.getStoreConfByLocale(ctx.locale, storeIdForPrice));
    } else {
      storeConf = await lastValueFrom(storeService.getStoreConfByCodeIso(ctx.locale, storeIdForPrice));
    }

    dispatch(setStoreConf({ storeConf }));

    if (storeConf?.storeId === STORE_ID.COM && !storeIdForPrice) {
      const cookieOptions = {
        path: '/',
        maxAge: COOKIES.STOREID_FOR_PRICE.LIFETIME
      };

      storeIdForPrice = STORE_ID.FRANCE;
      setCookie(ctx, COOKIES.STOREID_FOR_PRICE.NAME, storeIdForPrice, cookieOptions);
    }

    if (storeConf?.storeId !== STORE_ID.COM && storeIdForPrice) {
      storeIdForPrice = null;
      destroyCookie(ctx, COOKIES.STOREID_FOR_PRICE.NAME);
    }

    dispatch(setStoreIdForPrice({ storeIdForPrice }));

    // Redirect to private site authentication if needed
    if (storeConf.privateWebsite && !isConnected) {
      const currentPage = `/${ctx.resolvedUrl.split('/')[1].split('?')[0]}`;
      if (![PAGES.PRIVATE_WEBSITE, PAGES.AUTH].includes(currentPage)) {
        return {
          redirect: {
            destination: PAGES.PRIVATE_WEBSITE.replace('/', ''),
            permanent: false
          }
        };
      }
    }

    if (isConnected) {
      dispatch(connectedUserSuccess({ user: loggedUser }));
    }

    // Set ActAs mode
    if (loggedUser?.isAdmin && actAsCustomer) {
      dispatch(setActAsCustomer({ customer: actAsCustomer }));
    }

    // Valid token detected (user or guest), get specific infos
    if (authToken) {
      const isStepHT = stepHT.some((s) => ctx.resolvedUrl.includes(s));
      const [cart, box, user] = await completePromises([
        lastValueFrom(cartService.get(
          storeConf.storeId,
          isStepHT,
          storeIdForPrice
        )),
        lastValueFrom(boxService.get(storeConf.storeId, storeIdForPrice)),
        isConnected ? lastValueFrom(userService.get(storeConf.storeId)) : lastValueFrom(of())
      ]);
      if (cart) {
        if (!storeIdForPrice && cart.storeIdForPrice) {
          const cookieOptions = {
            path: '/',
            maxAge: COOKIES.STOREID_FOR_PRICE.LIFETIME
          };

          setCookie(ctx, COOKIES.STOREID_FOR_PRICE.NAME, cart.storeIdForPrice, cookieOptions);
        }
        dispatch(getCartSuccess({ content: cart }));
      }
      if (box) {
        dispatch(getBoxSuccess({ content: box }));
      }
      if (user) {
        dispatch(getUserSuccess({ user }));
        if (user.hasStorage) {
          const cartStorage: any = await lastValueFrom(storageService.getCartStorage());
          dispatch(getCartStorageSuccess({ cartStorage }));
        }
        if (user.wineBoxStorage) {
          dispatch(addItemToStorageWBOk({ storageWineBox: user.wineBoxStorage }));
        }
        if (user.wishList) {
          dispatch(getWishListSuccess({ products: user.wishList }));
        }
        if (user.email) {
          const { createHash } = await import('crypto');
          const md5 = createHash('md5').update(user.email).digest('hex');
          const sha256 = createHash('sha256').update(user.email).digest('hex');
          dispatch(encodeUserEmail({ md5, sha256 }));

          if (user.pickId) {
            const hashedPickId = createHash('sha256').update(user.pickId).digest('hex');
            dispatch(encodeUserPickId(hashedPickId));
          }
        }
      }
    }

    if (fullLayout) {
      // Full layout requested, else don't retrive menu and espots
      const espots: valueof<typeof ESPOTS>[] = [ESPOTS.HEADER_PROMOTION, ESPOTS.CART_SECURE_INFO, ESPOTS.FOOTER_LEGAL_NOTICE];

      const rating: RatingType = await lastValueFrom(externalService.getGlobalReview(storeConf.storeId));

      if (rating) {
        const ratingValue = rating?.ratingValue;
        const ratingCount = rating?.ratingCount;
        dispatch(setRatings({ ratingValue, ratingCount }));
      }

      const [menu, [promo, secureInfo, legalNotice]] = await completePromises([
        productsService.getMenu(storeConf.storeId),
        lastValueFrom(espotService.getEspotDataByLocations(storeConf.storeId, espots))
      ]);

      const emptyItemEspots = await lastValueFrom(
        espotService.getEspotByTypeAndStoreId('ESPOT_EMPTY_ITEM', storeConf.storeId)
      ) satisfies EmptyItemEspotType[];

      dispatch(setNavBarMenu({ menu }));
      dispatch(setLayoutEspots({ espots: { promo, secureInfo, legalNotice } }));
      dispatch(setEmptyItemEspots({ emptyItemEspots }));
    }

    log('EXIT', store.debugId, ctx.req?.url);
  } catch (error) {
    log('ERROR !!!!', store.debugId, ctx.req?.url);
    console.error('prefetchLayout error', error);
  }

  // Return the default page layout config
  return {
    locale: storeConf.locale || 'fr-FR',
    withPromotionHeader: true,
    withHeader: true,
    headerType: HEADER_TYPE.DEFAULT,
    withNavBar: true,
    withFooter: true,
    footerType: FOOTER_TYPE.DEFAULT,
    withScrollButton: false,
    withWineBoxModal: true,
    isGoogleBot: isGoogleBotSaved,
  };
};
